import React, { useRef, useEffect, useState } from 'react';
import TableChat from './table-chat/table-chat';
import { Helmet } from 'react-helmet-async';

interface TableChatSectionProps {
  className?: string;
  title?: string;
  showHeader?: boolean;
}

const TableChatSection: React.FC<TableChatSectionProps> = ({
  className = '',
  title = "Analyze Your Data, Your Way",
  showHeader = true,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [shouldRenderTableChat, setShouldRenderTableChat] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebComponent",
    "name": "Interactive Data Analysis Interface",
    "description": "A powerful data analysis tool that allows users to work with tables, documents, and use chat to transform information exactly how they need it.",
    "featureList": [
      "Interactive table interface",
      "Document processing",
      "AI-powered chat analysis",
      "Data transformation",
      "Real-time visualization"
    ]
  };

  // Add intersection observer to detect when the section is in view
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        // When component is in view, render it
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Small delay to ensure smooth transition
          const timer = setTimeout(() => {
            setShouldRenderTableChat(true);
          }, 100);
          return () => clearTimeout(timer);
        } else {
          // When out of view, unmount it
          setIsVisible(false);
          setShouldRenderTableChat(false);
        }
      },
      { 
        threshold: 0.1, // 10% visibility threshold
        rootMargin: '100px 0px' // Buffer to start loading before it's fully in view
      }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <section 
        ref={sectionRef} 
        className={`mt-8 mb-8 ${className} relative z-10 isolate`}
        aria-labelledby={showHeader ? "table-chat-title" : undefined}
      >
        {showHeader && (
          <header className="text-center mb-16">
            <h2 
              id="table-chat-title"
              className="text-3xl sm:text-4xl font-bold mb-4"
            >
              {title}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              After processing data with workflows, analyze without limitations. Seamlessly work with tables, 
              documents, and use chat to transform information exactly how you need it.
            </p>
          </header>
        )}
        <div 
          className="w-full rounded-[8px] shadow-lg border border-gray-200 overflow-hidden"
          role="region"
          aria-label="Interactive data analysis interface"
        >
          <div className="h-[594px] relative">
            <div 
              className={`absolute inset-0 transition-opacity duration-300 ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {shouldRenderTableChat && <TableChat />}
            </div>
            {!isVisible && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-gray-100"
                aria-hidden="true"
              >
                <div className="text-gray-600">Loading interactive interface...</div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default TableChatSection; 